<script setup lang="ts">
import { AppBookmarkableEnum } from "@/services/fobizzToolsApi";
import { type User } from "@/services/orval/internalApiV1";

const props = withDefaults(
  defineProps<{
    kind: AppBookmarkableEnum;
    materialId: string;
    authorId: string;
    bookmarksCount?: number;
    showBookmarksCount?: boolean;
    name: string;
  }>(),
  {
    showBookmarksCount: true,
    bookmarksCount: undefined,
  }
);

const userStore = useUserStore();
const isAuthor = computed(
  () => props.authorId === (userStore.user as User)?.toolsUserId
);

const { bookmarked, toggleBookmark } = useBookmark(
  props.kind as AppBookmarkableEnum,
  props.materialId,
  isAuthor
);
</script>

<template>
  <div
    v-if="!userStore.isAuthenticated || isAuthor || userStore.isVisitor"
    class="d-flex"
  >
    <div
      v-if="showBookmarksCount && bookmarksCount"
      tabindex="-1"
      class="material-counts material-counts--owner material-counts--no-action"
    >
      <i class="bi bi-heart"></i>
      <span>{{ bookmarksCount }}</span>
    </div>
  </div>

  <div v-else class="d-flex">
    <button
      class="btn btn-light material-counts position-relative"
      :class="{ 'material-counts--fill': bookmarked }"
      :aria-label="
        bookmarked ? `Remove bookmark ${name}` : `Add bookmark ${name}`
      "
      @click="toggleBookmark"
    >
      <i v-if="bookmarked" class="bi bi-heart-fill"></i>
      <i v-else class="bi bi-heart"></i>
      <span v-if="showBookmarksCount && bookmarksCount">
        {{ bookmarksCount }}
      </span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.material-counts {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background-color: $color-surface-white;
  border: 1px solid $gray-100;
  font-size: $font-size-sm;
  z-index: 1;

  &:hover {
    background-color: $gray-100;
  }

  i {
    font-size: $font-size-xs;
    height: 1.1rem;
  }

  @include media-breakpoint-down(sm) {
    padding: $btn-padding-y-sm $btn-padding-x-sm;
    font-size: $font-size-xs;

    i {
      font-size: $font-size-xxs;
      height: 1rem;
    }
  }

  &--fill {
    color: $color-system-meta;
  }

  &--no-action {
    cursor: default;
    pointer-events: none;

    @include media-breakpoint-down(sm) {
      padding-bottom: 0;
    }

    &:first-of-type {
      margin-right: $spacer-3;

      @include media-breakpoint-down(sm) {
        margin-right: $spacer-1;
      }
    }
  }

  &--owner {
    background-color: $color-surface-white;
    padding-right: 0;
    border: none;

    &:hover {
      background-color: $color-surface-white;
    }
  }
}
</style>
